import { useEffect, useRef, useState } from "react";
import "./QrCode.css";

export const CONFIG = {
  baseUrl: process.env.REACT_APP_QRCODE_BASE_URL,
  params: [
    {
      key: "action", label: "Action",
      options: ["DigitalOnboardingProcess", "FaceIDVerification", "DocumentCapture", "DocumentAuthenticity", "PresentCredential"].map(value => ({ label: value, value })),
    },
    {
      key: "organizationCode", label: "Organization",
      options: [
        { label: "Demo (1234)", value: "1234" },
        { label: "NIB (5678)", value: "5678" },
        // { label: "Stage (7777)", value: "7777" },  // TODO: should be uncomment after routing done
      ],
    },
  ],
};
const STORAGE_KEY = "url-qr-selection";
let libraryPromise;
function loadLibrary() {
  if (window.QRCode) return Promise.resolve(window.QRCode);
  if (!libraryPromise) {
    libraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";
      script.onload = () => window.QRCode ? resolve(window.QRCode) : reject(new Error("QR library unavailable"));
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return libraryPromise;
}
function loadSelection() {
  let saved;
  try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch {}
  return Object.fromEntries(CONFIG.params.map(p => [p.key,
    p.options.some(o => o.value === saved?.[p.key]) ? saved[p.key] : p.options[0].value,
  ]));
}
export function buildQrUrl(baseUrl, selection) {
  const url = new URL(baseUrl);
  if (url.protocol !== "https:") throw new Error("The QR code base URL must use HTTPS.");
  CONFIG.params.forEach(p => url.searchParams.delete(p.key));
  CONFIG.params.forEach(p => url.searchParams.append(p.key, selection[p.key]));
  return url.toString();
}
export function QrCodeScreen() {
  const [selection, setSelection] = useState(loadSelection);
  const [changed, setChanged] = useState(null);
  const [status, setStatus] = useState("");
  const [qrImage, setQrImage] = useState("");
  const [qrError, setQrError] = useState(false);
  const urlRef = useRef(null);
  let url = "";
  let error = "";
  try { url = buildQrUrl(CONFIG.baseUrl, selection); }
  catch { error = "Set REACT_APP_QRCODE_BASE_URL to a valid HTTPS URL and rebuild the app."; }

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "TS.DOS QR Code Generator";
    return () => { document.title = previousTitle; };
  }, []);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selection)); } catch {}
  }, [selection]);
  useEffect(() => {
    let active = true;
    setQrImage("");
    setQrError(false);
    if (url) loadLibrary().then(QRCode => {
      if (!active) return;
      const container = document.createElement("div");
      new QRCode(container, {
        text: url, width: 512, height: 512,
        colorDark: "#000000", colorLight: "#FFFFFF", correctLevel: QRCode.CorrectLevel.M,
      });
      setQrImage(container.querySelector("canvas").toDataURL("image/png"));
    }).catch(() => { if (active) setQrError(true); });
    return () => { active = false; };
  }, [url]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setStatus("URL copied.");
    } catch {
      const range = document.createRange();
      range.selectNodeContents(urlRef.current);
      const selected = window.getSelection();
      selected.removeAllRanges();
      selected.addRange(range);
      setStatus("URL selected. Press Ctrl+C (⌘C on Mac) to copy it.");
    }
  };
  // Split the actual serialized URL so display, clipboard and QR always agree.
  const parts = url ? url.split(/([?&](?:action|organizationCode)=[^&#]*)/g) : [];
  return (
    <div className="qrcode-page">
      <main>
        <h1>TS.DOS QR Code Generator</h1>
        <div className="qr-layout">
          <section>
            <div className="qr-fields">
              {CONFIG.params.map((p, index) => (
                <div className="qr-field" key={p.key}>
                  <label htmlFor={`qr-${p.key}`}>
                    <span className="qr-swatch" style={{ background: `var(--p${index + 1})` }} />
                    <span>{p.label}</span><span className="qr-key">{p.key}</span>
                  </label>
                  <div className="qr-select-wrap">
                    <select id={`qr-${p.key}`} value={selection[p.key]} style={{ borderLeftColor: `var(--p${index + 1})` }} onChange={event => {
                      setSelection({ ...selection, [p.key]: event.target.value });
                      setChanged(p.key); setStatus("");
                    }}>
                      {p.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="qr-url-box">
              <h2>Full URL</h2>
              {error ? <p role="alert">{error}</p> : <>
                <p className="qr-url" ref={urlRef}>{parts.map((part, i) => {
                  const index = CONFIG.params.findIndex(p => part.startsWith(`?${p.key}=`) || part.startsWith(`&${p.key}=`));
                  return index < 0 ? <span className="qr-base" key={i}>{part}</span> : <span key={`${i}-${part}`}><span className="qr-base">{part[0]}</span><span className={`qr-seg${changed === CONFIG.params[index].key ? " qr-flash" : ""}`} style={{ color: `var(--p${index + 1})`, background: `var(--p${index + 1}-soft)` }}>{part.slice(1, part.indexOf("=") + 1)}<strong>{part.slice(part.indexOf("=") + 1)}</strong></span></span>;
                })}</p>
                <div className="qr-actions">
                  <button className="qr-primary" type="button" onClick={copyUrl}>Copy URL</button>
                  <a className="qr-open" href={url} target="_blank" rel="noopener noreferrer">Open URL</a>
                </div>
              </>}
              <p className="qr-status" role="status" aria-live="polite">{status}</p>
            </div>
          </section>
          <aside className="qr-qr-panel">
            <div className="qr-qr-card"><div className="qr-image" aria-live="polite">
              {qrImage && <img src={qrImage} alt={`QR code for ${url}`} />}
              {qrError && <p role="alert">The QR code library couldn’t load. Reload the page to try again.</p>}
            </div></div>
            <p className="qr-qr-note">Right-click the image (or long-press on mobile) to save it.</p>
          </aside>
        </div>
      </main>
    </div>
  );
}
